import { AlertTriangle, Clock, ListChecks, MessageCircle } from "lucide-react";
import KpiCard from "shared/KpiCard";
import type { DueList } from "../api/OutreachTouchesApi";
import { defaultStage, noAnswer, waitingOnUs, type StageKey } from "../dueStages";
import { daysAgo } from "../outreachDates";

interface Props {
  data: DueList;
  /** Open the stage the box counts. */
  onOpen: (stage: StageKey) => void;
  /** Open the Inbox on the replies waiting on us (conversations are no longer a stage of Today). */
  onOpenInbox: () => void;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The day at a glance: four numbers, each in its own light colour like Smartlead's analytics tiles (purple, pink, teal, red) (To do turns to the warning colour while anything is overdue), that say what to do first. Each box opens the stage it counts.
 *  Always from the whole list (not the search), so the numbers do not move while you search. */
export default function DueStatBoxes({ data, onOpen, onOpenInbox }: Props) {
  const toDo = data.counts.due_total;
  const overdue = data.counts.overdue;
  const replies = waitingOnUs(data);
  const quiet = noAnswer(data);
  const waiting = data.groups.requests_to_check;
  const oldest = waiting.map((t) => t.sent_on).filter((d): d is string => !!d).sort()[0];
  const attention = data.groups.needs.length + data.groups.waiting_for_start.length;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" aria-label="Today at a glance" role="group">
      <KpiCard
        icon={ListChecks} label="To do today" value={toDo} tone={overdue > 0 ? "warning" : "brand"}
        hint={overdue > 0 ? `${overdue} overdue` : toDo === 0 ? "Nothing due" : "Nothing overdue"}
        onClick={() => onOpen(defaultStage(data))}
      />
      <KpiCard
        icon={MessageCircle} label="Replies waiting on us" value={replies} tone="magenta"
        hint={replies === 0 && quiet === 0 ? "All answered" : quiet > 0 ? `${quiet} gone quiet (no answer)` : "Answer them first"}
        onClick={onOpenInbox}
      />
      <KpiCard
        icon={Clock} label="Waiting for acceptance" value={waiting.length} tone="teal"
        hint={oldest ? `Oldest sent ${daysAgo(oldest) || "today"}` : "No requests out"}
        onClick={() => onOpen("waiting")}
      />
      <KpiCard
        icon={AlertTriangle} label="Needs attention" value={attention} tone="danger"
        hint={attention === 0 ? "Nothing to fix" : `${plural(attention, "card")} to fix or start`}
        onClick={() => onOpen("needs")}
      />
    </div>
  );
}
